import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import {
  CirclePlus,
  Unlink,
} from 'lucide-react'
import {
  useCallback,
  useState,
} from 'react'
import toast from 'react-hot-toast'

import ApiApplySuccess from '@/app/(main)/account-settings/components/api-apply-success'
import CreateExternalApiKeyDialog from '@/app/(main)/account-settings/components/create-external-api-key-dialog'
import { useConfirmDialog } from '@/components/confirm-dialog'
import Copy from '@/components/icons/copy.svg'
import Logo from '@/components/icons/logo.svg'
import { Button } from '@/components/ui/button'
import { formatAddress } from '@/lib/formatter'
import {
  useApplyApiKeyMutation,
  useReadAllPlatformsQuery,
  useReadMineApiKeyQuery,
  useReadMineExternalApiKeyQuery,
  useRemoveExternalApiKeyMutation,
} from '@/redux/api-key'

export default function Api() {
  const { data: allPlatforms } = useReadAllPlatformsQuery()
  const { data: mineApiKey } = useReadMineApiKeyQuery()
  const { data: mineExternalApiKeys } = useReadMineExternalApiKeyQuery()
  const [applyApiKey, { data: applyApiKeyData }] = useApplyApiKeyMutation()
  const [removeExternalApiKey] = useRemoveExternalApiKeyMutation()
  const [apiApplySuccessOpen, setApiApplySuccessOpen] = useState(false)
  const [createExternalOpen, setCreateExternalOpen] = useState(false)
  const firstPlatform = allPlatforms?.[0]

  const onCopyApiKey = useCallback(async () => {
    if (mineApiKey) {
      await navigator.clipboard.writeText(mineApiKey.key)
      toast.success('AxGrad API key copied')
    }
  }, [mineApiKey])

  const onCopyApiSecret = useCallback(async () => {
    if (mineApiKey) {
      await navigator.clipboard.writeText(mineApiKey.secret)
      toast.success('AxGrad API secret copied')
    }
  }, [mineApiKey])

  const confirm = useConfirmDialog()

  const onApplyApiKey = useCallback(async () => {
    confirm({
      title: 'Confirm to create your API Key',
      description: 'This key will grant access to your account’s data. Make sure to store it securely.',
      onConfirm: async () => {
        await applyApiKey().unwrap()
        setApiApplySuccessOpen(true)
      },
    })
  }, [applyApiKey, confirm])

  const onCreateExternalApiKey = useCallback(() => {
    setCreateExternalOpen(true)
  }, [])

  return (
    <div>
      <div className="text-lg mb-2">
        AxGrad API
      </div>
      {mineApiKey ? (
        <>
          <div className="text-xs text-tertiary-foreground mb-6">
            Please make sure you securely store your API Key and Secret and avoid sharing them with
            others.
          </div>
          <div className="flex gap-4">
            <div className="px-6 py-3 rounded-xl border border-white/8 bg-white/5 flex flex-1 gap-2 items-center">
              <Logo className="size-10 bg-primary rounded-full p-1" />
              <div className="text-sm">
                {mineApiKey.key}
              </div>
              <Copy
                className="size-5 cursor-pointer fill-tertiary-foreground"
                onClick={onCopyApiKey}
              />
            </div>
            <VisuallyHidden className="px-6 py-3 rounded-xl border border-white/8 bg-white/5">
              <div className="text-xs text-secondary-foreground">
                API Secret
              </div>
              <div className="flex gap-1">
                <div className="text-xs text-secondary-foreground">
                  {formatAddress({ start: 4, end: 4 })(mineApiKey.secret)}
                </div>
                <Copy
                  className="size-5 cursor-pointer fill-tertiary-foreground"
                  onClick={onCopyApiSecret}
                />
              </div>
            </VisuallyHidden>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <div className="text-sm text-secondary-foreground">
            You can only apply one API Key currently
          </div>
          <Button variant="primary" onClick={onApplyApiKey}>Apply API Key</Button>
        </div>
      )}
      <ApiApplySuccess
        open={apiApplySuccessOpen}
        onOpenChange={setApiApplySuccessOpen}
        apiKey={applyApiKeyData?.key}
        apiSecret={applyApiKeyData?.secret}
      />

      <VisuallyHidden>
        <div className="text-lg mb-2 mt-16">
          Trading API
        </div>
        <ol className="text-white/50 text-xs list-decimal list-inside mb-6">
          <li>
            Kindly furnish the third-party API credentials (key and secret, e.g., Binance Futures
            Testnet) to enable trading via your AxGrad API key and server.
          </li>
          <li>
            It is important to acknowledge that event participation will be restricted to users who
            have executed a requisite volume of operations through these APIs.
          </li>
        </ol>
        {mineExternalApiKeys?.map(({ id, platformId, key }) => {
          const platform = allPlatforms?.find((p) => p.id === platformId)
          return (
            <div className="flex gap-8" key={id}>
              <div className="px-6 h-16 flex items-center flex-1 gap-2">
                {platform && <img className="size-10 p-2 bg-white/4 rounded-full"
                                  src={platform.logo}
                                  alt={platform.name} />}
                <div className="text-sm">{key}</div>
              </div>
              <div
                className="flex items-center gap-1 cursor-pointer"
                onClick={() => {
                  confirm({
                    title: 'Confirm API Disconnection',
                    description: 'After disconnection, linked services will stop working immediately.This action is permanent.',
                    confirmText: 'Disconnect',
                    confirmVariant: 'destructive',
                    onConfirm: async () => {
                      await removeExternalApiKey(id).unwrap()
                      toast.success('Disconnected')
                    },
                  })
                }}
              >
                <Unlink className="size-3" />
                <div className="text-sm">Disconnect</div>
              </div>
            </div>
          )
        })}
        <div
          className="text-muted-foreground flex gap-2 items-center justify-center h-16 rounded-xl border border-dashed border-muted-foreground cursor-pointer"
          onClick={onCreateExternalApiKey}
        >
          <CirclePlus />
          <div>Add API key of other trading platforms</div>
        </div>
        {firstPlatform && (
          <CreateExternalApiKeyDialog
            open={createExternalOpen}
            onOpenChange={setCreateExternalOpen}
            platform={firstPlatform}
          />
        )}
      </VisuallyHidden>
    </div>
  )
}
